import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import {  Spin, Input, Select, Tooltip, Button, message, Modal, Form, InputNumber, Popconfirm} from "antd";
import { EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from "moment/moment";
import { Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, Legend, ResponsiveContainer, LabelList } from 'recharts';
import ReportsTotalSummary from './ReportsTotalSummary';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from "../logo.jpg";

const { Option } = Select;


export default function Reports() {
  // Purchase state
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [incomingData, setIncomingData] = useState({
    monthlyPaymentSummary: [],
    supplierInvoices: []
  });
  const [incomingLoading, setIncomingLoading] = useState(true);

  // Sales state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [outgoingData, setOutgoingData] = useState({
    monthlyReceivableSummary: [],
    customerInvoices: []
  });
  const [outgoingLoading, setOutgoingLoading] = useState(true);

  // Purchase Items state
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [purchaseItemsSupplier, setPurchaseItemsSupplier] = useState('');

  // Sales Items state
  const [salesItems, setSalesItems] = useState([]);
  const [salesItemsCustomer, setSalesItemsCustomer] = useState('');

  function reloadReportsData() {
    // For efficiency, only reload the outgoing (sales) summary/invoices
    let url = '/reports/outgoing-summary';
    if (selectedCustomer) {
      url += `?customerId=${selectedCustomer}`;
    }
    fetch(url)
        .then(res => res.json())
        .then(resData => setOutgoingData(resData));
  }
  // Fetch suppliers
  useEffect(() => {
    fetch('/suppliers')
        .then(res => res.json())
        .then(setSuppliers)
        .catch(() => setSuppliers([]));
  }, []);

  // Fetch customers
  useEffect(() => {
    fetch('/customers')
        .then(res => res.json())
        .then(setCustomers)
        .catch(() => setCustomers([]));
  }, []);

  // Fetch incoming data
  useEffect(() => {
    setIncomingLoading(true);
    let url = '/reports/summary';
    if (selectedSupplier) {
      url += `?supplierId=${selectedSupplier}`;
    }
    fetch(url)
        .then(res => res.json())
        .then(resData => {
          setIncomingData(resData);
          setIncomingLoading(false);
        })
        .catch(() => setIncomingLoading(false));
  }, [selectedSupplier]);

  // Fetch outgoing data
  useEffect(() => {
    setOutgoingLoading(true);
    let url = '/reports/outgoing-summary';
    if (selectedCustomer) {
      url += `?customerId=${selectedCustomer}`;
    }
    fetch(url)
        .then(res => res.json())
        .then(resData => {
          setOutgoingData(resData);
          setOutgoingLoading(false);
        })
        .catch(() => setOutgoingLoading(false));
  }, [selectedCustomer]);

  // Fetch purchase items
  useEffect(() => {
    fetch('/reports/purchase-items')
        .then(res => res.json())
        .then(setPurchaseItems)
        .catch(() => setPurchaseItems([]));
  }, []);

  // Fetch sales items
  useEffect(() => {
    fetch('/reports/sales-items')
        .then(res => res.json())
        .then(setSalesItems)
        .catch(() => setSalesItems([]));
  }, []);

  return (
      <div style={{ padding: 24 }}>

          {/* Add ReportsTotalSummary here */}
          <ReportsTotalSummary />

        <h2 style={{
          fontSize: 16,
          fontWeight: 500,
          fontFamily: '"Roboto","Helvetica Neue",Arial,"sans-serif"',
          marginBottom: 24
        }}>
          <b>Invoice & Payment/Receivable Dashboard</b>
        </h2>
        <Tabs>
          <TabList>
            <Tab>Purchase Invoices</Tab>
            <Tab>Sales Invoices</Tab>
            <Tab>Purchase Items</Tab>
            <Tab>Sales Items</Tab>
            <Tab>Export</Tab>
          </TabList>

          {/* Purchase Tab */}
          <TabPanel>
            <div style={{ marginBottom: 20 }}>
              <label>
                <b>Filter by Supplier: </b>
                <select
                    value={selectedSupplier}
                    onChange={e => setSelectedSupplier(e.target.value)}
                    style={{ marginLeft: 8, padding: 4 }}
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map(s => (
                      <option key={s.SupplierID} value={s.SupplierID}>{s.SupplierName}</option>
                  ))}
                </select>
              </label>
            </div>
            <h4>Monthly Purchase Invoice vs Payments</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={incomingData.monthlyPaymentSummary}>
                <XAxis dataKey="Month" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="InvoiceTotal" fill="#0000ff" name="Invoice Total">
                  <LabelList dataKey="InvoiceTotal" position="top" />
                </Bar>
                <Bar dataKey="PaymentsTotal" fill="#D46600" name="Payments">
                  <LabelList dataKey="PaymentsTotal" position="top" />
                </Bar>
                <Bar dataKey="TDSTotal" fill="#00ff00" name="TDS">
                  <LabelList dataKey="TDSTotal" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <SupplierInvoiceTable
                supplierInvoices={incomingData.supplierInvoices}
                loading={incomingLoading}
            />
          </TabPanel>

          {/* Sales Tab */}
          <TabPanel>
            <div style={{ marginBottom: 20 }}>
              <label>
                <b>Filter by Customer: </b>
                <select
                    value={selectedCustomer}
                    onChange={e => setSelectedCustomer(e.target.value)}
                    style={{ marginLeft: 8, padding: 4 }}
                >
                  <option value="">All Customers</option>
                  {customers.map(c => (
                      <option key={c.CustomerID} value={c.CustomerID}>{c.CustomerName}</option>
                  ))}
                </select>
              </label>
            </div>
            <h4>Monthly Sales Invoice vs Receipts</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={outgoingData.monthlyReceivableSummary}>
                <XAxis dataKey="Month" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="InvoiceTotal" fill="#0000ff" name="Invoice Total">
                  <LabelList dataKey="InvoiceTotal" position="top" />
                </Bar>
                <Bar dataKey="ReceivablesTotal" fill="#D46600" name="Receipts">
                  <LabelList dataKey="ReceivablesTotal" position="top" />
                </Bar>
                <Bar dataKey="TDSTotal" fill="#00ff00" name="TDS">
                  <LabelList dataKey="TDSTotal" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <CustomerInvoiceTable
                customerInvoices={outgoingData.customerInvoices}
                loading={outgoingLoading}
                reloadReportsData={reloadReportsData}
            />
          </TabPanel>

          {/* Purchase Items Tab */}
          <TabPanel>
            <PurchaseItemsTable
                items={purchaseItems}
                suppliers={suppliers}
                selectedSupplier={purchaseItemsSupplier}
                setSelectedSupplier={setPurchaseItemsSupplier}
            />
          </TabPanel>

          {/* Sales Items Tab */}
          <TabPanel>
            <SalesItemsTable
                items={salesItems}
                customers={customers}
                selectedCustomer={salesItemsCustomer}
                setSelectedCustomer={setSalesItemsCustomer}
            />
          </TabPanel>

          {/* Export Tab */}
          <TabPanel>
            <ExportTab />
          </TabPanel>
        </Tabs>
      </div>
  );
}
////////////////////////////////////////////////////////////////////////////////////
// --- Sales Invoices/  Customer → Invoice → Receipts (FLAT, FILTERABLE, SPINNER) ---
export function CustomerInvoiceTable({
                                       customerInvoices = [],
                                       loading = false,
                                       reloadReportsData // Make sure this is passed as a prop if you need it here
                                     }) {
  // --- All state at the top ---
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editingFields, setEditingFields] = useState({});



  // Flatten rows: one per receipt, or one per invoice if no receipts
  const rows = useMemo(() => {
    const arr = [];


    customerInvoices.forEach(customer => {
      customer.Invoices.forEach(inv => {
        if (inv.Receivables && inv.Receivables.length > 0) {
          inv.Receivables.forEach(rec => {
            arr.push({
              CustomerName: customer.CustomerName,
              GST: customer.GST,
              InvoiceNumber: inv.InvoiceNumber,
              PO_Number: inv.PO_Number,
              Status: inv.Status,
              Client_Updated: inv.Client_Updated,
              Destination: inv.Destination,
              Date: inv.Date,
              RateAmount: inv.RateAmount,
              TaxAmount: inv.TaxAmount,
              TotalAmount: inv.TotalAmount,
              InvoiceTDS: inv.InvoiceTDS,
              PendingAmount: rec.PendingAmount != null ? Number(rec.PendingAmount).toFixed(2) : "",
              ReceivableID: rec.ReceivableID,
              ReceivableDate: rec.ReceivableDate,
              AmountReceived: rec.AmountReceived,
              ReceivableTDS: rec.TDS
            });
          });
        } else {
          arr.push({
            CustomerName: customer.CustomerName,
            GST: customer.GST,
            InvoiceNumber: inv.InvoiceNumber,
            PO_Number: inv.PO_Number,
            Status: inv.Status,
            Client_Updated: inv.Client_Updated,
            Destination: inv.Destination,
            Date: inv.Date,
            RateAmount: inv.RateAmount,
            TotalAmount: inv.TotalAmount,
            TaxAmount: inv.TaxAmount,
            InvoiceTDS: inv.InvoiceTDS,
            PendingAmount: "",
            ReceivableID: "",
            ReceivableDate: "",
            AmountReceived: "",
            ReceivableTDS: ""
          });
        }
      });
    });
    return arr;
  }, [customerInvoices]);


  // handler for edit/delete Receipts
  function handleEditReceiptSubmit() {
    const { AmountReceived, TDS, ReceivableDate } = editingFields;
    fetch(`/reports/receipts/${editingReceipt.ReceivableID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ AmountReceived, TDS, ReceivableDate })
    })
        .then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(() => {
          message.success("Receipt updated!");
          setEditingReceipt(null);
          setEditingFields({});
          if (typeof reloadReportsData === "function") reloadReportsData();
        })
        .catch(() => message.error("Failed to update receipt"));
  }

  function handleDeleteReceipt(receiptId) {
    fetch(`/reports/receipts/${receiptId}`, { method: "DELETE" })
        .then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(() => {
          message.success("Receipt deleted.");
          if (typeof reloadReportsData === "function") reloadReportsData();
        })
        .catch(() => message.error("Failed to delete receipt"));
  }



    // --- FILTERS ---
  const [customerFilter, setCustomerFilter] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState(null);
  const [destinationFilter, setDestinationFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(['Partial', 'Pending']); // default selection
  const [clientUpdatedFilter, setClientUpdatedFilter] = useState([]);
  //const [poNumberFilter, setPoNumberFilter] = useState([]);
  const [poFilter, setPoFilter] = useState([]);

    // Helper to get unique values in filter
const unique = arr => Array.from(new Set(arr.filter(Boolean)));

// filtered rows for csv download:
    const filteredRows = useMemo(() => {
        return rows.filter(row => {
            if (customerFilter.length && !customerFilter.includes(row.CustomerName)) return false;
            if (invoiceFilter.length && !invoiceFilter.includes(row.InvoiceNumber)) return false;
            if (destinationFilter.length && !destinationFilter.includes(row.Destination)) return false;
            if (statusFilter.length && !statusFilter.includes(row.Status)) return false;
            if (poFilter.length && !poFilter.includes(row.PO_Number)) return false;
            if (clientUpdatedFilter.length) {
                // Treat null/undefined as empty string
                const val = row.Client_Updated || '';
                if (!clientUpdatedFilter.includes(val)) return false;
            }
            if (dateFilter) {
                const m = dateFilter.match(/^([<>]=?|=)\s*(\d{4}-\d{2}-\d{2})$/);
                if (m) {
                    const op = m[1];
                    const dateStr = m[2];
                    if (op === ">" && !moment(row.Date).isAfter(dateStr, "day")) return false;
                    if (op === "<" && !moment(row.Date).isBefore(dateStr, "day")) return false;
                    if (op === ">=" && !moment(row.Date).isSameOrAfter(dateStr, "day")) return false;
                    if (op === "<=" && !moment(row.Date).isSameOrBefore(dateStr, "day")) return false;
                    if (op === "=" && !moment(row.Date).isSame(dateStr, "day")) return false;
                }
            }
            return true;
        });
    }, [rows, customerFilter, invoiceFilter, poFilter, dateFilter, destinationFilter, statusFilter]);


    //for filter
    const clientUpdatedOptions = useMemo(() => {
        const set = new Set();
        customerInvoices.forEach(customer => {
            customer.Invoices.forEach(inv => {
                if (inv.Client_Updated === 'Y' || inv.Client_Updated === 'N') {
                    set.add(inv.Client_Updated);
                } else {
                    set.add('');
                }
            });
        });
        return Array.from(set);
    }, [customerInvoices]);

    //for filter
  const allDestinations = useMemo(() => {
    const set = new Set();
    customerInvoices.forEach(customer => {
      customer.Invoices.forEach(inv => {
        if (inv.Destination) set.add(inv.Destination);
      });
    });
    return Array.from(set);
  }, [customerInvoices]);

 // Compute options for each filter, excluding itself from filtering
    const customerOptions = useMemo(() => unique(
        rows.filter(row =>
            (!invoiceFilter.length || invoiceFilter.includes(row.InvoiceNumber)) &&
            (!destinationFilter.length || destinationFilter.includes(row.Destination)) &&
            (!statusFilter.length || statusFilter.includes(row.Status)) &&
            (!dateFilter || /* your date logic, see original code */ true)
        ).map(row => row.CustomerName)
    ), [rows, invoiceFilter, destinationFilter, statusFilter, dateFilter]);

    const invoiceOptions = useMemo(() => unique(
        rows.filter(row =>
            (!customerFilter.length || customerFilter.includes(row.CustomerName)) &&
            (!destinationFilter.length || destinationFilter.includes(row.Destination)) &&
            (!statusFilter.length || statusFilter.includes(row.Status)) &&
            (!dateFilter || /* your date logic, see original code */ true)
        ).map(row => row.InvoiceNumber)
    ), [rows, customerFilter, destinationFilter, statusFilter, dateFilter]);

    const destinationOptions = useMemo(() => unique(
        rows.filter(row =>
            (!customerFilter.length || customerFilter.includes(row.CustomerName)) &&
            (!invoiceFilter.length || invoiceFilter.includes(row.InvoiceNumber)) &&
            (!statusFilter.length || statusFilter.includes(row.Status)) &&
            (!dateFilter || /* your date logic, see original code */ true)
        ).map(row => row.Destination)
    ), [rows, customerFilter, invoiceFilter, statusFilter, dateFilter]);

    const statusOptions = useMemo(() => unique(
        rows.filter(row =>
            (!customerFilter.length || customerFilter.includes(row.CustomerName)) &&
            (!invoiceFilter.length || invoiceFilter.includes(row.InvoiceNumber)) &&
            (!destinationFilter.length || destinationFilter.includes(row.Destination)) &&
            (!dateFilter || /* your date logic, see original code */ true)
        ).map(row => row.Status)
    ), [rows, customerFilter, invoiceFilter, destinationFilter, dateFilter]);

    const poOptions = useMemo(() => unique(
        rows.filter(row =>
            (!customerFilter.length || customerFilter.includes(row.CustomerName)) &&
            (!invoiceFilter.length || invoiceFilter.includes(row.InvoiceNumber)) &&
            (!destinationFilter.length || destinationFilter.includes(row.Destination)) &&
            (!statusFilter.length || statusFilter.includes(row.Status)) &&
            (!dateFilter || true) // keep same pattern as others
        ).map(row => row.PO_Number)
    ), [rows, customerFilter, invoiceFilter, destinationFilter, statusFilter, dateFilter]);


    useEffect(() => {
        setCustomerFilter(old => old.filter(val => customerOptions.includes(val)));
        setInvoiceFilter(old => old.filter(val => invoiceOptions.includes(val)));
        setDestinationFilter(old => old.filter(val => destinationOptions.includes(val)));
        setStatusFilter(old => old.filter(val => statusOptions.includes(val)));
        setPoFilter(old => old.filter(val => poOptions.includes(val)));
    }, [customerOptions, invoiceOptions, destinationOptions, statusOptions, poOptions]);



    // Reset filters
  const resetFilters = () => {
    setCustomerFilter([]);
    setInvoiceFilter([]);
    setPoFilter([]);
    setDateFilter("");
    setStatusFilter(['Partial', 'Pending']);
    setDestinationFilter([]);
  };

  // --- CSV Export ---
    const csvHeaders = [
        "Customer","GST","Invoice Number","PO Number","Status","Client_Updated","Destination","Date","Rate Amount",
        "Tax Amount","Total Amount","Receivable ID","Receivable Date",
        "Amount Received","Receivable TDS","Pending Amount"
    ];

    const csvHeaderKeyMap = {
        "Customer": "CustomerName",
        "GST": "GST",
        "Invoice Number": "InvoiceNumber",
        "PO Number": "PO_Number",
        "Status": "Status",
        "Client_Updated": "Client_Updated",
        "Destination": "Destination",
        "Date": "Date",
        "Rate Amount": "RateAmount",
        "Tax Amount": "TaxAmount",
        "Total Amount": "TotalAmount",
        "Receivable ID": "ReceivableID",
        "Receivable Date": "ReceivableDate",
        "Amount Received": "AmountReceived",
        "Receivable TDS": "ReceivableTDS",
        "Pending Amount": "PendingAmount"
    };

    function downloadCSV() {
        const csvRows = [
            csvHeaders.join(","),
            ...filteredRows.map(row =>
                csvHeaders.map(h => {
                    const key = csvHeaderKeyMap[h];
                    const val = key ? row[key] : "";
                    if (val === undefined || val === null) return "";
                    return `"${String(val).replace(/"/g, '""')}"`;
                }).join(",")
            )
        ].join("\r\n");

        const blob = new Blob([csvRows], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "customer_invoice_report.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

///  invoice only csv export start
// Invoice-only CSV headers with latest receipt info
    const invoiceOnlyHeaders = [
        "Customer",
        "GST",
        "Invoice Number",
        "PO Number",
        "Status",
        "Client_Updated",
        "Destination",
        "Date",
        "Rate Amount",
        "Tax Amount",
        "Total Amount",
        "Receivable TDS",
        "Pending Amount",
        "Amount Received"
    ];

    const invoiceOnlyHeaderKeyMap = {
        "Customer": "CustomerName",
        "GST": "GST",
        "Invoice Number": "InvoiceNumber",
        "PO Number": "PO_Number",
        "Status": "Status",
        "Client_Updated": "Client_Updated",
        "Destination": "Destination",
        "Date": "Date",
        "Rate Amount": "RateAmount",
        "Tax Amount": "TaxAmount",
        "Total Amount": "TotalAmount",
        "Receivable TDS": "ReceivableTDS",
        "Pending Amount": "PendingAmount",
        "Amount Received": "AmountReceived"
    };

// Build flattened invoice-only rows with latest receipt info
    const invoiceRowsWithLatestReceipt = useMemo(() => {
        const arr = [];
        customerInvoices.forEach(customer => {
            customer.Invoices.forEach(inv => {
                let latestReceipt = null;
                if (inv.Receivables && inv.Receivables.length > 0) {
                    latestReceipt = inv.Receivables.reduce((max, rec) =>
                        !max || rec.ReceivableID > max.ReceivableID ? rec : max, null);
                }

                // Debug log for latestReceipt
                console.log('Latest Receipt for Invoice:', inv.InvoiceNumber, latestReceipt);


                arr.push({
                    CustomerName: customer.CustomerName,
                    GST: customer.GST,
                    InvoiceNumber: inv.InvoiceNumber,
                    PO_Number: inv.PO_Number,
                    Status: inv.Status,
                    Client_Updated: inv.Client_Updated,
                    Destination: inv.Destination,
                    Date: inv.Date,
                    RateAmount: inv.RateAmount,
                    TaxAmount: inv.TaxAmount,
                    TotalAmount: inv.TotalAmount,
                    ReceivableTDS: latestReceipt ? latestReceipt.TDS : "",
                    PendingAmount: latestReceipt ? latestReceipt.PendingAmount : "",
                    AmountReceived: latestReceipt ? latestReceipt.AmountReceived : "",
                });
            });
        });
        return arr;
    }, [customerInvoices]);

// Filtered invoice rows applying relevant filters
    const filteredInvoiceRowsWithReceipt = useMemo(() => {
        return invoiceRowsWithLatestReceipt.filter(row => {
            if (customerFilter.length && !customerFilter.includes(row.CustomerName)) return false;
            if (invoiceFilter.length && !invoiceFilter.includes(row.InvoiceNumber)) return false;
            if (destinationFilter.length && !destinationFilter.includes(row.Destination)) return false;
            if (statusFilter.length && !statusFilter.includes(row.Status)) return false;
            if (clientUpdatedFilter.length) {
                const val = row.Client_Updated || "";
                if (!clientUpdatedFilter.includes(val)) return false;
            }
            if (dateFilter) {
                const m = dateFilter.match(/^([<>]=?|=)\s*(\d{4}-\d{2}-\d{2})$/);
                if (m) {
                    const op = m[1];
                    const dateStr = m[2];
                    if (op === ">" && !moment(row.Date).isAfter(dateStr, "day")) return false;
                    if (op === "<" && !moment(row.Date).isBefore(dateStr, "day")) return false;
                    if (op === ">=" && !moment(row.Date).isSameOrAfter(dateStr, "day")) return false;
                    if (op === "<=" && !moment(row.Date).isSameOrBefore(dateStr, "day")) return false;
                    if (op === "=" && !moment(row.Date).isSame(dateStr, "day")) return false;
                }
            }
            return true;
        });
    }, [invoiceRowsWithLatestReceipt, customerFilter, invoiceFilter, destinationFilter, statusFilter, clientUpdatedFilter, dateFilter]);

// Function to export invoice-only CSV
    function downloadInvoiceOnlyCSV() {
        const csvRows = [
            invoiceOnlyHeaders.join(","),
            ...filteredInvoiceRowsWithReceipt.map(row =>
                invoiceOnlyHeaders.map(h => {
                    const map = {
                        "Customer": "CustomerName",
                        "GST": "GST",
                        "Invoice Number": "InvoiceNumber",
                        "PO Number": "PO_Number",
                        "Status": "Status",
                        "Client_Updated": "Client_Updated",
                        "Destination": "Destination",
                        "Date": "Date",
                        "Rate Amount": "RateAmount",
                        "Tax Amount": "TaxAmount",
                        "Total Amount": "TotalAmount",
                        "Receivable TDS": "ReceivableTDS",
                        "Pending Amount": "PendingAmount",
                        "Amount Received": "AmountReceived"
                    };
                    const val = row[map[h]];
                    if (val === undefined || val === null) return "";
                    return `"${String(val).replace(/"/g, '""')}"`;
                }).join(",")
            )
        ].join("\r\n");

        const blob = new Blob([csvRows], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "customer_invoice_only_report.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
////   invoice only csv export end

    return (
      <div style={{ marginTop: 24 }}>
        <h4>Customer → Invoice → Receipts</h4>
        {/* FILTERS */}
        <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {/* Customer Filter */}
            <Select
                mode="multiple"
                value={customerFilter}
                onChange={setCustomerFilter}
                placeholder="Customer"
                allowClear
                style={{ minWidth: 180, marginRight: 8 }}
            >
                {customerOptions.map(option => (
                    <Select.Option key={option} value={option}>
                        {option}
                    </Select.Option>
                ))}
            </Select>
            {/* PO Number Filter */}
            <Select
                mode="multiple"
                value={poFilter}
                onChange={setPoFilter}
                placeholder="PO Number"
                allowClear
                style={{ minWidth: 160 }}
            >
                {poOptions.map(option => (
                    <Select.Option key={option} value={option}>
                        {option}
                    </Select.Option>
                ))}
            </Select>

            {/* Invoice Filter */}
          <Select
              showSearch
              mode="multiple"
              allowClear
              placeholder="Invoice Number"
              style={{ width: 140 }}
              value={invoiceFilter || undefined}
              onChange={val => setInvoiceFilter(val)}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {invoiceOptions.map(inv => (
                <Option key={inv} value={inv}>{inv}</Option>
            ))}
          </Select>
            {/* Status Filter */}
            <Select
                mode="multiple"
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="Status"
                allowClear
                style={{ minWidth: 140 }}
            >
                {statusOptions.map(option => (
                    <Select.Option key={option} value={option}>
                        {option}
                    </Select.Option>
                ))}
            </Select>

            {/* Client_Updated Filter */}
            <Select
                mode="multiple"
                value={clientUpdatedFilter}
                onChange={setClientUpdatedFilter}
                placeholder="Client Updated"
                allowClear
                style={{ minWidth: 150, marginRight: 8 }}
            >
                {clientUpdatedOptions.map(option => (
                    <Select.Option key={option} value={option}>
                        {option === '' ? '(empty)' : option}
                    </Select.Option>
                ))}
            </Select>


            {/* Destination Filter */}
            <Select
                mode="multiple"
                value={destinationFilter}
                onChange={setDestinationFilter}
                placeholder="Destination"
                allowClear
                style={{ minWidth: 150, marginRight: 8 }}
            >
                {destinationOptions.map(option => (
                    <Select.Option key={option} value={option}>
                        {option}
                    </Select.Option>
                ))}
            </Select>

          <Tooltip title="Type: >2025-01-01, <2025-01-01, >=2025-01-01, =2025-01-01">
            <Input
                placeholder="Date (e.g. >2025-01-01)"
                value={dateFilter || ""}
                onChange={e => setDateFilter(e.target.value)}
                style={{ width: 170 }}
            />
          </Tooltip>
           <Button onClick={resetFilters}>Reset</Button>
          <Button type="primary" onClick={downloadCSV}>
            Export CSV
          </Button>
            <Button type="primary" onClick={downloadInvoiceOnlyCSV}>
                Export Invoice_Only CSV
            </Button>
        </div>
        <Spin spinning={loading}>
          <table border="1" cellPadding={6} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
            <tr style={{ background: "#f0f0f0" }}>
              {csvHeaders.map(h => (
                  <th key={h}>{h}</th>
              ))}

            </tr>
            </thead>
            <tbody>
            {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={12}>No data available.</td>
                </tr>
            ) : (
                filteredRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.CustomerName}</td>
                      <td>{row.GST}</td>
                      <td>{row.InvoiceNumber}</td>
                      <td>{row.PO_Number}</td>
                      <td>{row.Status}</td>
                      <td>{row.Client_Updated ?? "Empty"}</td>
                      <td>{row.Destination}</td>
                      <td>{row.Date}</td>
                      <td>{row.RateAmount}</td>
                      <td>{Number(row.TaxAmount).toFixed(2)}</td>
                      <td>{row.TotalAmount}</td>
                      <td>{row.ReceivableID}</td>
                      <td>{row.ReceivableDate}</td>
                      <td>{row.AmountReceived}</td>
                      <td>{row.ReceivableTDS}</td>
                      <td>{row.PendingAmount}</td>
                      <td>
                        {row.ReceivableID && (
                            <>
                              <Tooltip title="Edit Receipt">
                                <Button
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={() => {
                                      setEditingReceipt(row);
                                      setEditingFields({
                                        AmountReceived: row.AmountReceived,
                                        TDS: row.ReceivableTDS,
                                        ReceivableDate: row.ReceivableDate
                                      });
                                    }}
                                    style={{ marginRight: 4 }}
                                />
                              </Tooltip>
                              <Tooltip title="Delete Receipt">
                                <Popconfirm
                                    title="Delete this payment?"
                                    onConfirm={() => handleDeleteReceipt(row.ReceivableID)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                  <Button
                                      icon={<DeleteOutlined />}
                                      size="small"
                                      danger
                                  />
                                </Popconfirm>
                              </Tooltip>
                            </>
                        )}
                      </td>

                    </tr>
                ))
            )}
            </tbody>
          </table>
        </Spin>
        <Modal
            open={!!editingReceipt}
            title="Edit Receipt"
            onCancel={() => setEditingReceipt(null)}
            onOk={handleEditReceiptSubmit}
            okText="Update"
            destroyOnClose
        >
          <Form
              layout="vertical"
              initialValues={editingFields}
              onValuesChange={(_, allFields) => setEditingFields(allFields)}
          >
            <Form.Item name="AmountReceived" label="Amount Received" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="TDS" label="TDS" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="ReceivableDate" label="Receipt Date" rules={[{ required: true }]}>
              <Input type="date" />
            </Form.Item>
          </Form>
        </Modal>

      </div>
  );
}
//////////////////////////////////////////////////////////////////////////////////////////
// --- Purchase Invoices/ Supplier → Invoice → Payments (FLAT, FILTERABLE) ---
export function SupplierInvoiceTable({ supplierInvoices = [], loading = false }) {
  // Flatten rows
  const rows = useMemo(() => {
    const arr = [];
    supplierInvoices.forEach(supplier => {
      supplier.Invoices.forEach(inv => {
        if (inv.Payments && inv.Payments.length > 0) {
          inv.Payments.forEach(pay => {
            arr.push({
              SupplierName: supplier.SupplierName,
              InvoiceID:inv.InvoiceID,
              InvoiceNumber: inv.InvoiceNumber,
              Status: inv.Status,
              Destination: inv.Destination,
              Date: inv.Date,
              RateAmount: inv.RateAmount,
              TaxAmount: inv.TaxAmount,
              TotalAmount: inv.TotalAmount,
              PendingAmount: pay.PendingAmount,
              PaymentID: pay.PaymentID,
              PaymentDate: pay.PaymentDate,
              AmountPaid: pay.AmountPaid,
              PaymentTDS: pay.TDS,
              Paid_Via: pay.Paid_Via,
              UTR_Ref: pay.UTR_Ref
            });
          });
        } else {
          arr.push({
            SupplierName: supplier.SupplierName,
            InvoiceID:inv.InvoiceID,
            InvoiceNumber: inv.InvoiceNumber,
            Status: inv.Status,
            Destination:inv.Destination,
            Date: inv.Date,
            RateAmount: inv.RateAmount,
            TaxAmount: inv.TaxAmount,
            TotalAmount: inv.TotalAmount,
            PendingAmount: "",
            PaymentID: "",
            PaymentDate: "",
            AmountPaid: "",
            PaymentTDS: "",
            Paid_Via: "",
            UTR_Ref: ""
          });
        }
      });
    });
    return arr;
  }, [supplierInvoices]);


    // --- FILTERS ---
  const [supplierFilter, setSupplierFilter] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(['Partial', 'Pending']);

  // Unique options for filters
  const supplierOptions = useMemo(
      () => [...new Set(rows.map(r => r.SupplierName).filter(Boolean))],
      [rows]
  );
  const invoiceOptions = useMemo(
      () => [...new Set(rows.map(r => r.InvoiceNumber).filter(Boolean))],
      [rows]
  );
  const statusOptions = ['Partial', 'Pending', 'Full'];

  // Apply filters
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      if (supplierFilter.length && !supplierFilter.includes(row.SupplierName)) return false;
      if (invoiceFilter.length && !invoiceFilter.includes(row.InvoiceNumber)) return false;
      if (statusFilter.length && !statusFilter.includes(row.Status)) return false;
       if (dateFilter) {
        const m = dateFilter.match(/^([<>]=?|=)\s*(\d{4}-\d{2}-\d{2})$/);
        if (m) {
          const op = m[1];
          const dateStr = m[2];
          if (op === ">" && !moment(row.Date).isAfter(dateStr, "day")) return false;
          if (op === "<" && !moment(row.Date).isBefore(dateStr, "day")) return false;
          if (op === ">=" && !moment(row.Date).isSameOrAfter(dateStr, "day")) return false;
          if (op === "<=" && !moment(row.Date).isSameOrBefore(dateStr, "day")) return false;
          if (op === "=" && !moment(row.Date).isSame(dateStr, "day")) return false;
        }
      }
      return true;
    });
  }, [rows, supplierFilter, invoiceFilter, statusFilter,dateFilter]);


  // Reset filters
  const resetFilters = () => {
    setSupplierFilter([]);
    setInvoiceFilter([]);
    setStatusFilter(['Partial', 'Pending']); // Reset to default
    setDateFilter("");
  };

  // --- CSV Export ---
    const csvHeaders = [
        "Supplier",
        "InvoiceID",
        "Invoice Number",
        "Status",
        "Destination",
        "Date",
        "Rate Amount",
        "Tax Amount (GST)",
        "Total Amount",
        "Payment ID",
        "Payment Date",
        "Amount Paid",
        "TDS Deducted",
        "Pending Amount",
        "Paid_Via",
        "UTR_Ref"
    ];

    const csvHeaderKeyMap = {
        "Supplier": "SupplierName",
        "InvoiceID": "InvoiceID",
        "Invoice Number": "InvoiceNumber",
        "Status": "Status",
        "Destination": "Destination",
        "Date": "Date",
        "Rate Amount": "RateAmount",
        "Tax Amount (GST)": "TaxAmount",
        "Total Amount": "TotalAmount",
        "Payment ID": "PaymentID",
        "Payment Date": "PaymentDate",
        "Amount Paid": "AmountPaid",
        "TDS Deducted": "PaymentTDS",
        "Pending Amount": "PendingAmount",
        "Paid_Via": "Paid_Via",
        "UTR_Ref": "UTR_Ref"
    };

// for Supplier Invoice Payments reports
    function downloadCSV() {
        const csvRows = [
            csvHeaders.join(","),
            ...filteredRows.map(row =>
                csvHeaders.map(h => {
                    const key = csvHeaderKeyMap[h];
                    const val = key ? row[key] : "";
                    if (val === undefined || val === null) return "";
                    return `"${String(val).replace(/"/g, '""')}"`;
                }).join(",")
            )
        ].join("\r\n");

        const blob = new Blob([csvRows], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "supplier_invoice_report.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    function loadImageAsDataUrl(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";

            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);

                const dataUrl = canvas.toDataURL("image/png");
                resolve(dataUrl);
            };

            img.onerror = reject;
            img.src = src;
        });
    }
    async function downloadPDF() {
        const doc = new jsPDF("l", "mm", "a4");
        const pdfHeaders = [
            "Invoice Number",
            "Destination",
            "Date",
            "Rate Amount",
            "Tax Amount (GST)",
            "Total Amount",
            "Payment Date",
            "Amount Paid",
            "TDS Deducted",
            "Paid_Via",
            "UTR_Ref"
        ];

        const toNumber = (value) => {
            if (value === undefined || value === null || value === "") return 0;
            const cleaned = String(value).replace(/,/g, "").trim();
            const num = parseFloat(cleaned);
            return isNaN(num) ? 0 : num;
        };

        const fmt2 = (value) => {
            const num = toNumber(value);
            return num.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        };

        const isNumericPdfColumn = (header) => [
            "Rate Amount",
            "Tax Amount (GST)",
            "Total Amount",
            "Amount Paid",
            "TDS Deducted"
        ].includes(header);

        let logoDataUrl = null;
        try {
            logoDataUrl = await loadImageAsDataUrl(logo);
        } catch (e) {
            console.error("Logo load failed", e);
        }
        if (logoDataUrl) {
            doc.addImage(logoDataUrl, "PNG", 225, 8, 35, 20);
        }

        const headers = pdfHeaders;
        const body = filteredRows.map(row =>
            headers.map(h => {
                const key = csvHeaderKeyMap[h];
                const val = key ? row[key] : "";

                if (val === undefined || val === null || val === "") return "";

                if (isNumericPdfColumn(h)) {
                    return fmt2(val);
                }

                return String(val);
            })
        );

        const totalAmountKey = csvHeaderKeyMap["Total Amount"];
        const amountPaidKey = csvHeaderKeyMap["Amount Paid"];
        const PaymentTDSKey = csvHeaderKeyMap["TDS Deducted"];

        const invoiceNumberKey = csvHeaderKeyMap["Invoice Number"];
        const uniqueInvoiceTotals = new Map();

        filteredRows.forEach((row) => {
            const invoiceNo = row[invoiceNumberKey];
            if (!invoiceNo) return;

            if (!uniqueInvoiceTotals.has(invoiceNo)) {
                uniqueInvoiceTotals.set(invoiceNo, toNumber(row[totalAmountKey]));
            }
        });

        const A = Array.from(uniqueInvoiceTotals.values()).reduce((sum, val) => sum + val, 0);

        const B = filteredRows.reduce((sum, row) => sum + toNumber(row[amountPaidKey]), 0);
        const C = filteredRows.reduce((sum, row) => sum + toNumber(row[PaymentTDSKey]), 0);

        const D = A - B - C;

        const fmt = (n) =>
            Number(n || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });

        const supplierLabel =
            supplierFilter.length === 1
                ? supplierFilter[0]
                : supplierFilter.length > 1
                    ? supplierFilter.join(", ")
                    : "All Suppliers";

        const statementDate = moment().format("DD-MM-YYYY");

        //doc.setFontSize(14);
        //doc.text("Supplier Invoice Payments Report", 14, 15);

        autoTable(doc, {
            startY: 22,
            body: [
                ["Supplier", supplierLabel],
                ["Statement Date", statementDate]
            ],
            theme: "grid",
            styles: {
                fontSize: 10,
                cellPadding: 3
            },
            columnStyles: {
                0: { fontStyle: "bold", cellWidth: 40, fillColor: [245, 245, 245] },
                1: { cellWidth: 90 }
            },
            margin: { left: 14 }
        });

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 6,
            body: [
                ["Billed Amount", `Rs. ${fmt(A)}`],
                ["TDS Amount", `Rs. ${fmt(C)}`],
                ["Payment Made", `Rs. ${fmt(B)}`],
                ["Outstanding", `Rs. ${fmt(D)}`]
            ],
            theme: "grid",
            styles: {
                fontSize: 10,
                cellPadding: 3
            },
            columnStyles: {
                0: { fontStyle: "bold", cellWidth: 40, fillColor: [245, 245, 245] },
                1: { cellWidth: 50 }
            },
            margin: { left: 14 }
        });

        autoTable(doc, {
            head: [headers],
            body,
            startY: doc.lastAutoTable.finalY + 10,
            styles: {
                fontSize: 8,
                cellPadding: 1,
                halign: "center"
            },
            columnStyles: {
                0: { cellWidth: 30, halign: "left" },
                1: { cellWidth: 50, halign: "left" },
                2: { cellWidth: 22, halign: "center" },
                3: { cellWidth: 20, halign: "right" },
                4: { cellWidth: 18, halign: "right" },
                5: { cellWidth: 18, halign: "right" },
                6: { cellWidth: 22, halign: "center" },
                7: { cellWidth: 18, halign: "right" },
                8: { cellWidth: 18, halign: "right" },
                9: { cellWidth: 25, halign: "center" },
                10: { cellWidth: 25, halign: "center" }
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: "bold"
            },
            theme: "grid",
            margin: { left: 10, right: 10 },
            rowPageBreak: 'avoid',
            didDrawPage: function (data) {
                const pageHeight = doc.internal.pageSize.getHeight();
                doc.setFontSize(8);
                doc.text(`Page ${doc.getNumberOfPages()}`, data.settings.margin.left, pageHeight - 5);
            }
        });

        let noteY = doc.lastAutoTable.finalY + 10;
        const pageHeight = doc.internal.pageSize.getHeight();

        if (noteY > pageHeight - 15) {
            doc.addPage();
            noteY = 15;
        }

        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        doc.text(
            "This is a system-generated ledger statement and does not require any signature.",
            14,
            noteY
        );
        doc.save("supplier_invoice_report.pdf");
    }
    ///////


  return (
      <div style={{ marginTop: 24 }}>
        <h4>Supplier → Invoice → Payments</h4>
        {/* FILTERS */}
        <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Select
              showSearch
              mode="multiple"
              allowClear
              placeholder="Supplier"
              style={{ width: 180 }}
              value={supplierFilter || undefined}
              onChange={val => setSupplierFilter(val)}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {supplierOptions.map(s => (
                <Option key={s} value={s}>{s}</Option>
            ))}
          </Select>

          <Select
              showSearch
              mode="multiple"
              allowClear
              placeholder="Invoice Number"
              style={{ width: 140 }}
              value={invoiceFilter || undefined}
              onChange={val => setInvoiceFilter(val)}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {invoiceOptions.map(inv => (
                <Option key={inv} value={inv}>{inv}</Option>
            ))}
          </Select>
          <Select
              mode="multiple"
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              showSearch
          >
            {statusOptions.map(opt => (
                <Option key={opt} value={opt}>{opt}</Option>
            ))}
          </Select>


          <Tooltip title="Type: >2025-01-01, <2025-01-01, >=2025-01-01, =2025-01-01">
            <Input
                placeholder="Date (e.g. >2025-01-01)"
                value={dateFilter || ""}
                onChange={e => setDateFilter(e.target.value)}
                style={{ width: 170 }}
            />
          </Tooltip>
           <Button onClick={resetFilters}>Reset</Button>
          <Button type="primary" onClick={downloadCSV}>
            Export CSV
          </Button>
            <Button type="primary" onClick={downloadPDF}>
                Export PDF
            </Button>
        </div>
        <Spin spinning={loading}>
          <table border="1" cellPadding={6} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
            <tr style={{ background: "#f0f0f0" }}>
              {csvHeaders.map(h => (
                  <th key={h}>{h}</th>
              ))}
            </tr>
            </thead>
            <tbody>
            {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={12}>No data available.</td>
                </tr>
            ) : (
                filteredRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.SupplierName}</td>
                        <td>{row.InvoiceID}</td>
                      <td>{row.InvoiceNumber}</td>
                      <td>{row.Status}</td>
                      <td>{row.Destination}</td>
                      <td>{row.Date}</td>
                      <td>{row.RateAmount}</td>
                      <td>{row.TaxAmount}</td>
                      <td>{row.TotalAmount}</td>
                      <td>{row.PaymentID}</td>
                      <td>{row.PaymentDate}</td>
                      <td>{row.AmountPaid}</td>
                      <td>{row.PaymentTDS}</td>
                      <td>{row.PendingAmount}</td>
                      <td>{row.Paid_Via}</td>
                      <td>{row.UTR_Ref}</td>
                    </tr>
                ))
            )}
            </tbody>
          </table>
        </Spin>
      </div>
  );
}

function ExportTab() {
  const exportTables = [
    { name: "SupplierMaster", label: "Suppliers" },
    { name: "CustomerMaster", label: "Customers" },
    { name: "PurchaseInvoice", label: "Purchase Invoices" },
    { name: "SalesInvoice", label: "Sales Invoices" },
    { name: "Payments", label: "Payments" },
    { name: "Receipts", label: "Receipts" },
    { name: "PurchaseItems", label: "Purchase Items" },
    { name: "SalesItems", label: "Sales Items" }
  ];
  return (
      <div>
        <h3>Export Database & Table Data</h3>
        <div style={{ marginBottom: 16 }}>
          <a
              href="/export/db"
              download="invoicing.db"
              style={{
                marginRight: 8,
                padding: '8px 16px',
                fontWeight: 'bold',
                display: 'inline-block',
                background: '#1677ff',
                color: '#fff',
                borderRadius: 4,
                textDecoration: 'none'
              }}
          >
            <DownloadOutlined /> Export Full DB File
          </a>
        </div>
        <div>
          <a
              href="/export/all-tables-zip"
              download="invoicing_tables.zip"
              style={{
                marginRight: 8,
                padding: '8px 16px',
                fontWeight: 'bold',
                display: 'inline-block',
                background: '#faad14',
                color: '#222',
                borderRadius: 4,
                textDecoration: 'none'
              }}
          >
            <DownloadOutlined /> Export All Tables as ZIP
          </a>
        </div>
        <div>
          <h4>Export Individual Tables as CSV</h4>
          {exportTables.map(tbl => (
              <a
                  key={tbl.name}
                  href={`/export/table?name=${tbl.name}`}
                  download={`${tbl.label}.csv`}
                  style={{
                    marginRight: 8,
                    marginBottom: 8,
                    padding: '6px 12px',
                    display: 'inline-block',
                    background: '#13c2c2',
                    color: '#fff',
                    borderRadius: 4,
                    textDecoration: 'none'
                  }}
              >
                <DownloadOutlined /> {tbl.label}
              </a>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
            <h4>GST Reports (Pipe-delimited CSV)</h4>
            <a href="/export/gst-current-month"
               download="gst-current-month.psv"
               style={{ marginRight: 8, padding: '8px 16px', background: '#52c41a', color: 'white', borderRadius: 4, textDecoration: 'none', fontWeight: 'bold' }}>
                <DownloadOutlined /> GST Report (Current Month)
            </a>
            <a href="/export/gst-prev-month"
               download="gst-prev-month.psv"
               style={{ marginRight: 8, padding: '8px 16px', background: '#faad14', color: 'white', borderRadius: 4, textDecoration: 'none', fontWeight: 'bold' }}>
                <DownloadOutlined /> GST Report (Previous Month)
            </a>

        </div>

      </div>
  );
}
/////////////////////////////////////////////////////////////////////////////////////////
// --- Sales Items Table ---
export function SalesItemsTable({ items = [], customers = [] }) {
  // Filter states
  const [invoiceFilter, setInvoiceFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState(null);
  const [customerFilter, setCustomerFilter] = useState([]);
  const [totalAmountFilter, setTotalAmountFilter] = useState("");

  // Filtering logic
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (invoiceFilter.length && !invoiceFilter.includes(item.InvoiceNumber)) return false;
      if (customerFilter.length && !customerFilter.includes(item.CustomerName)) return false;

      // Date filter as before, supports operators
      if (dateFilter) {
        const m = dateFilter.match(/^([<>]=?|=)\s*(\d{4}-\d{2}-\d{2})$/);
        if (m) {
          const op = m[1];
          const dateStr = m[2];
          if (op === ">" && !moment(item.Date).isAfter(dateStr, "day")) return false;
          if (op === "<" && !moment(item.Date).isBefore(dateStr, "day")) return false;
          if (op === ">=" && !moment(item.Date).isSameOrAfter(dateStr, "day")) return false;
          if (op === "<=" && !moment(item.Date).isSameOrBefore(dateStr, "day")) return false;
          if (op === "=" && !moment(item.Date).isSame(dateStr, "day")) return false;
        }
      }

      // Total Amount filter as before, power-user style
      if (totalAmountFilter) {
        const str = typeof totalAmountFilter === "string" ? totalAmountFilter.trim() : "";
        const m = str.match(/^([<>]=?|={1,2})\s*(-?\d+(\.\d+)?)/);
        const val = Number(item.Total);
        if (m) {
          const op = m[1];
          const num = parseFloat(m[2]);
          switch (op) {
            case ">": if (!(val > num)) return false; break;
            case "<": if (!(val < num)) return false; break;
            case ">=": if (!(val >= num)) return false; break;
            case "<=": if (!(val <= num)) return false; break;
            case "=":
            case "==": if (!(val === num)) return false; break;
            default: break;
          }
        } else if (!String(item.Total).includes(str)) {
          return false;
        }
      }

      return true;
    });
  }, [items, invoiceFilter, customerFilter, dateFilter, totalAmountFilter]);

  // CSV Export
  const csvHeaders = [
    "Invoice Number",
    "Date",
    "Customer",
    "GST",
    "Item Name",
    "UOM",
    "SAC Code",
    "Rate",
    "Quantity",
    "Base Amount",
    "CGST (%)",
    "SGST (%)",
    "IGST (%)",
    "Tax Amount",
    "Total"
  ];

  function downloadCSV() {
    const csvRows = [
      csvHeaders.join(","),
      ...filteredItems.map(item =>
          [
            item.InvoiceNumber,
            item.Date,
            item.CustomerName,
            item.GST,
            item.ItemName,
            item.UOM,
            item.SAC_Code,
            item.Rate,
            item.Quantity,
            item.RateAmount,
            item.CGST,
            item.SGST,
            item.IGST,
            item.TaxAmount,
            item.Total
          ].map(val => `"${String(val ?? "").replace(/"/g, '""')}"`).join(",")
      )
    ].join("\r\n");
    const blob = new Blob([csvRows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sales_items.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Reset filters
  const resetFilters = () => {
    setInvoiceFilter([]);
    setDateFilter("");
    setCustomerFilter([]);
    setTotalAmountFilter("");
  };

  return (
      <div style={{ marginTop: 24 }}>
        <h4>Sales Items</h4>
        {/* Filter Controls */}
        <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Invoice Number"
              style={{ width: 140 }}
              value={invoiceFilter || undefined}
              onChange={setInvoiceFilter}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {Array.from(new Set(items.map(item => item.InvoiceNumber).filter(Boolean))).map(inv => (
                <Option key={inv} value={inv}>{inv}</Option>
            ))}
          </Select>

          <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Customer"
              style={{ width: 140 }}
              value={customerFilter}
              onChange={setCustomerFilter}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {Array.from(new Set(items.map(item => item.CustomerName).filter(Boolean))).map(inv => (
                <Option key={inv} value={inv}>{inv}</Option>
            ))}
          </Select>

          <Tooltip title="Type: >2025-01-01, <2025-01-01, >=2025-01-01, =2025-01-01">
            <Input
                placeholder="Date (e.g. >2025-01-01)"
                value={dateFilter || ""}
                onChange={e => setDateFilter(e.target.value)}
                style={{ width: 170 }}
            />
          </Tooltip>

          <Input
              placeholder="Total Amount (e.g. >1000)"
              value={totalAmountFilter}
              onChange={e => setTotalAmountFilter(e.target.value)}
              style={{ width: 170 }}
          />
          <Button onClick={resetFilters}>Reset</Button>
          <Button type="primary" onClick={downloadCSV}>Export CSV</Button>
        </div>
        <table border="1" cellPadding={6} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
          <tr style={{ background: "#f0f0f0" }}>
            {csvHeaders.map(h => <th key={h}>{h}</th>)}
          </tr>
          </thead>
          <tbody>
          {filteredItems.length === 0 ? (
              <tr><td colSpan={13}>No data available.</td></tr>
          ) : filteredItems.map((item, idx) => (
              <tr key={item.ItemID || idx}>
                <td>{item.InvoiceNumber}</td>
                <td>{item.Date}</td>
                <td>{item.CustomerName}</td>
                <td>{item.GST}</td>
                <td>{item.ItemName}</td>
                <td>{item.UOM}</td>
                <td>{item.SAC_Code}</td>
                <td>{item.Rate}</td>
                <td>{item.Quantity}</td>
                <td>{item.RateAmount}</td>
                <td>{item.CGST}</td>
                <td>{item.SGST}</td>
                <td>{item.IGST}</td>
                <td>{item.TaxAmount}</td>
                  <td>{item.Total != null ? Number(Number(item.Total).toFixed(2)) : ""}</td>
              </tr>
          ))}
          </tbody>
        </table>
      </div>
  );
}
//////////////////////////////////////////////////////////////////////////////////////////
// --- Purchase Items Table ---
export function PurchaseItemsTable({ items = [], suppliers = [] }) {
  // Filter states
  const [invoiceFilter, setInvoiceFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState(null);
  const [supplierFilter, setSupplierFilter] = useState([]);
  const [totalAmountFilter, setTotalAmountFilter] = useState([]);


  // Filtering logic
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (invoiceFilter.length && !invoiceFilter.includes(item.InvoiceNumber)) return false;
      if (supplierFilter.length && !supplierFilter.includes(item.SupplierName)) return false;
      // Date and totalAmountFilter logic as before...
      if (dateFilter) {
        // [date matching code unchanged]
      }
      if (totalAmountFilter) {
        const m = typeof totalAmountFilter === "string" ? totalAmountFilter.trim().match(/^([<>]=?|={1,2})\s*(-?\d+(\.\d+)?)/) : null;
        const val = Number(item.Total);
        if (m) {
          const op = m[1];
          const num = parseFloat(m[2]);
          switch (op) {
            case ">": if (!(val > num)) return false; break;
            case "<": if (!(val < num)) return false; break;
            case ">=": if (!(val >= num)) return false; break;
            case "<=": if (!(val <= num)) return false; break;
            case "=":
            case "==": if (!(val === num)) return false; break;
            default: break;
          }
        } else if (!String(item.Total).includes(totalAmountFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [items, invoiceFilter, dateFilter, supplierFilter, totalAmountFilter]);


  // CSV Export
  const csvHeaders = [
    "Invoice Number",
    "Date",
    "Supplier",
    "Item Name",
    "UOM",
    "SAC Code",
    "Rate",
    "Quantity",
    "Rate Amount",
    "CGST (%)",
    "SGST (%)",
    "IGST (%)",
    "Tax Amount",
    "Total"
  ];



  function downloadCSV() {
    const csvRows = [
      csvHeaders.join(","),
      ...filteredItems.map(item =>
          [
            item.InvoiceNumber,
            item.Date,
            item.SupplierName,
            item.ItemName,
            item.UOM,
            item.SAC_Code,
            item.Rate,
            item.Quantity,
            item.RateAmount,
            item.CGST,
            item.SGST,
            item.IGST,
            item.TaxAmount,
            item.Total
          ].map(val => `"${String(val ?? "").replace(/"/g, '""')}"`).join(",")
      )
    ].join("\r\n");
    const blob = new Blob([csvRows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "purchase_items.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Reset filters
  const resetFilters = () => {
    setInvoiceFilter([]);
    setDateFilter("");
    setSupplierFilter([]);
    setTotalAmountFilter([]);
  };

  return (
      <div style={{ marginTop: 24 }}>
        <h4>Purchase Items</h4>
        {/* Filter Controls */}
        <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Invoice Number"
              style={{ width: 140 }}
              value={invoiceFilter}
              onChange={setInvoiceFilter}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {Array.from(new Set(items.map(item => item.InvoiceNumber).filter(Boolean))).map(inv => (
                <Option key={inv} value={inv}>{inv}</Option>
            ))}
          </Select>


          <Select
              showSearch
              mode="multiple"
              allowClear
              placeholder="Supplier"
              style={{ width: 180 }}
              value={supplierFilter || undefined}
              onChange={setSupplierFilter}
              filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().startsWith(input.toLowerCase())
              }
          >
            {Array.from(new Set(items.map(item => item.SupplierName).filter(Boolean))).map(s => (
                <Option key={s} value={s}>{s}</Option>
            ))}
          </Select>
          <Tooltip title="Type: >2025-01-01, <2025-01-01, >=2025-01-01, =2025-01-01">
            <Input
                placeholder="Date (e.g. >2025-01-01)"
                value={dateFilter || ""}
                onChange={e => setDateFilter(e.target.value)}
                style={{ width: 170 }}
            />
          </Tooltip>


          <Input
              placeholder="Total Amount (e.g. >1000)"
              value={totalAmountFilter}
              onChange={e => setTotalAmountFilter(e.target.value)}
              style={{ width: 170 }}
          />
          <Button onClick={resetFilters}>Reset</Button>
          <Button type="primary" onClick={downloadCSV}>Export CSV</Button>
        </div>
        <table border="1" cellPadding={6} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
          <tr style={{ background: "#f0f0f0" }}>
            {csvHeaders.map(h => <th key={h}>{h}</th>)}
          </tr>
          </thead>
          <tbody>
          {filteredItems.length === 0 ? (
              <tr><td colSpan={13}>No data available.</td></tr>
          ) : filteredItems.map((item, idx) => (
              <tr key={item.ItemID || idx}>
                <td>{item.InvoiceNumber}</td>
                <td>{item.Date}</td>
                <td>{item.SupplierName}</td>
                <td>{item.ItemName}</td>
                <td>{item.UOM}</td>
                <td>{item.SAC_Code}</td>
                <td>{item.Rate}</td>
                <td>{item.Quantity}</td>
                <td>{item.RateAmount}</td>
                <td>{item.CGST}</td>
                <td>{item.SGST}</td>
                <td>{item.IGST}</td>
                <td>{item.TaxAmount}</td>
                <td>{item.Total}</td>
              </tr>
          ))}
          </tbody>
        </table>
      </div>
  );
}